import { DocumentNode, gql } from '@apollo/client';
import { parse, print, visit } from 'graphql';
import { ComponentProps } from 'react';
import type BlogIndexPage from '../pages/blog';
import type { BlogIndexPageProps } from '../pages/blog';
import type { BlogPageArchiveData } from '../src/components/BlogPage';
import CareerPostPage from '../src/components/CareerPostPage';
import Navbar from '../src/components/Navbar';
import type { NewsPageData } from '../src/components/NewsPage';
import type { BlogPostType } from '../src/modules/BlogPost';
import Page from '../src/Page';
import {
  NewsItem, Redirect, ResourceLibraryItem, ResourceLibraryPage,
} from './types';

interface APIResponse<T> {
  data: T,
  errors?: {message: string, extensions: {
    contentful: {
      code: 'UNRESOLVABLE_LINK' | string,
    }
  }}[],
}

type Props = {
  query: DocumentNode,
  url: string,
  token: string,
  variables?: Record<string, unknown>,
}

function getGqlString(doc: DocumentNode | string) {
  return typeof doc === 'string' ? doc : doc.loc?.source.body || '';
}

// Remove unused fragments from a query, needs to be run recursively to remove all unused.
function removeUnusedFragment(document: DocumentNode): [DocumentNode, number] {
  const usedFragmentSet = new Set<string>();
  let fragmentCount = 0;

  visit(document, {
    FragmentSpread(node) {
      usedFragmentSet.add(node.name.value);
    },
  });

  const doc = visit(document, {
    FragmentDefinition(node) {
      fragmentCount++;
      if (usedFragmentSet.has(node.name.value)) {
        return undefined;
      }
      fragmentCount--;
      return null;
    },
  });

  return [doc, fragmentCount];
}

export const defaultFragments = gql`

fragment image on Asset { url, description, width, title, height, contentType, __typename }
fragment link on AnyLink {
  ... on AnyLink {
    title
    url
    newTab
    image { ...image }
    page {
      ... on Page {
        slug
        __typename
      }

      ... on BlogPost {
        slug
        __typename
      }
    }
    wistiaVideoId
    __typename
  }
}
fragment imageWithId on Asset {
  sys { id }
  ...image
}
fragment button on Button { title, label, url, __typename }
`;

/* MIT License - Copyright (c) 2018 Jane (https://github.com/jane/gql-compress) */
export const minifyGQL = (content: string): string => content
  .replace(/(\b|\B)\s+(\b|\B)/gm, ' ')
  .replace(/(\B)\s+(\B)|(\b)\s+(\B)|(\B)\s+(\b)/gm, '').trim();

function recurseRemoveUnusedFragments(
  document: DocumentNode,
  oldFragmentCount?: number,
): DocumentNode {
  const [doc, fragmentCount] = removeUnusedFragment(document);
  // No change in fragment count, all fragments are used
  if (fragmentCount === oldFragmentCount) {
    return parse(print(doc));
  }
  // Recurse to attempt to remove more fragments
  return recurseRemoveUnusedFragments(doc, fragmentCount);
}

export async function request<T>({
  query,
  url,
  token,
  variables = {},
} : Props): Promise<T> {
  // Can probably be done in a better way without needing to re-parse the query.
  const queryWithFragments = parse(`${getGqlString(defaultFragments)}\n${getGqlString(query)}`);
  const fullQuery = minifyGQL(getGqlString(recurseRemoveUnusedFragments(queryWithFragments)));
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      // Contentful only allows up to 8kB query size
      query: fullQuery,
      variables,
    }),
  });
  const { data, errors: possibleErrors } = await res.json() as APIResponse<T>;
  if (possibleErrors) {
    const warnings: APIResponse<T>['errors'] = [];
    const errors = possibleErrors.filter((e) => {
      if (e.extensions?.contentful?.code === 'UNRESOLVABLE_LINK') {
        warnings.push(e);
        return false;
      }
      return true;
    });
    // eslint-disable-next-line no-console
    warnings.forEach((e) => console.warn('Warning:', e.message));
    errors.forEach((error) => {
      let message = `Error for query ${fullQuery} with variables ${JSON.stringify(variables)} ...:`;
      message += '\n';
      message += error.message;
      // eslint-disable-next-line no-console
      console.error(message);
    });
  }
  return data;
}

export interface CMSInterface {
  getWelcomePage(): Promise<{page: ComponentProps<typeof Page>}>
  getPageBySlug(slug: string): Promise<{page: ComponentProps<typeof Page>}>
  getNavbar(): Promise<{menu: ComponentProps<typeof Navbar>}>
  getStaticPaths(): Promise<Array<{slug: string}>>
  getPageById(id: string, preview: boolean, environment: string):
    Promise<{page: ComponentProps<typeof Page>}>
  getTotalNumberOfBlogPosts(categories: string[], search?: string): Promise<{
    numberOfPosts: number;
    categories: {[count: string]: number} }>
  getPaginatedBlogPostsEntries(
    skip: number, categories: string[], search?: string):
      Promise<{blogPost: Array<BlogIndexPageProps>}>
  getBlogPostWithSlug(slug: string, preview: boolean): Promise<[BlogPostType, ComponentProps<typeof BlogIndexPage>['blogPageArchive']['popularPosts'], ComponentProps<typeof BlogIndexPage>['blogPageArchive']['bottomPageLinks']]>
  getCareerListingPage(): Promise<typeof CareerPostPage>
  getBlogPostWithId(id: string, preview: boolean): ReturnType<CMSInterface['getBlogPostWithSlug']>;
  getBlogPostsPaths(): Promise<Array<{slug: string}>>;
  getNewsPostsPaths(): Promise<{slug: string, type: string}[]>;
  getBlogPageArchive(): Promise<BlogPageArchiveData>;
  getRedirects(): Promise<Redirect[]>;
  getWhitelistedTypes(types: string[]): Promise<string[]>;
  getWhitelistedCategories(categories: string[]): Promise<string[]>;
  getResourceCounts(types: string[], query: string): Promise<{
    total: number;
    typeCounts: {[type: string]: number};
  }>;
  getTotalNumberOfNews(type?: string): Promise<number>;
  getNewsPosts(type: string | undefined, page: number, customLimit?: number): Promise<NewsItem[]>;
  getNewsPage(): Promise<NewsPageData>;
  getResourceLibraryItems(
    pageIndex: number, perPage: number,
    types: string[],
    query: string
  ): Promise<ResourceLibraryItem[]>;
  getResourceLibraryTypes(): Promise<string[]>;
  getNewsPostBySlug(type: string, slug: string): Promise<NewsItem & { slug: string }>;
  getNewsPostById(id: string, preview: boolean): Promise<NewsItem>
  getResourceLibraryPage(): Promise<ResourceLibraryPage>;
}
